import * as THREE from 'three';

export function createMetalMaterial(color: number = 0xc0ccd8): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    metalness: 0.7,
    roughness: 0.3,
    envMapIntensity: 1.0,
  });
}

export function createGlassMaterial(): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: 0xaaccee,
    metalness: 0.0,
    roughness: 0.05,
    transparent: true,
    opacity: 0.3,
    transmission: 0.9,
    thickness: 0.02,
    side: THREE.DoubleSide,
  });
}

export function createFrostMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xd0e8ff,
    metalness: 0.0,
    roughness: 0.9,
    transparent: true,
    opacity: 0.4,
    side: THREE.DoubleSide,
  });
}

export function createPlasticMaterial(color: number = 0xdddddd): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    metalness: 0.0,
    roughness: 0.6,
  });
}

export function createSampleMaterial(color: string): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    metalness: 0.1,
    roughness: 0.4,
    transparent: true,
    opacity: 0.85,
  });
}

export function createGlowMaterial(color: string): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color: new THREE.Color(color),
    transparent: true,
    opacity: 0.9,
  });
}

export function createFloorMaterial(): THREE.MeshStandardMaterial {
  // Kórházi csempe padló - canvas textúra
  const size = 2048;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  const tileCount = 6;
  const tileSize = size / tileCount;
  const groutWidth = 4;

  // Fuga háttér - sötétebb szürke
  ctx.fillStyle = '#8a94a0';
  ctx.fillRect(0, 0, size, size);

  for (let row = 0; row < tileCount; row++) {
    for (let col = 0; col < tileCount; col++) {
      const x = col * tileSize + groutWidth;
      const y = row * tileSize + groutWidth;
      const w = tileSize - groutWidth * 2;
      const h = tileSize - groutWidth * 2;

      // Csempe alap - enyhe szín variáció sakktábla-szerűen
      const checker = (row + col) % 2 === 0;
      const baseR = checker ? 210 : 200;
      const baseG = checker ? 218 : 210;
      const baseB = checker ? 228 : 222;
      const v = Math.random() * 6 - 3;
      ctx.fillStyle = `rgb(${baseR + v}, ${baseG + v}, ${baseB + v})`;
      ctx.fillRect(x, y, w, h);

      // Finom márványos foltok a csempén
      for (let f = 0; f < 12; f++) {
        const fx = x + Math.random() * w;
        const fy = y + Math.random() * h;
        const fr = 15 + Math.random() * 40;
        const gradient = ctx.createRadialGradient(fx, fy, 0, fx, fy, fr);
        const brightness = Math.random() > 0.5 ? 'rgba(255,255,255,' : 'rgba(170,180,195,';
        gradient.addColorStop(0, brightness + (0.03 + Math.random() * 0.05) + ')');
        gradient.addColorStop(1, brightness + '0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, w, h);
      }

      // Fényes él felül-bal (tükröződés)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.fillRect(x, y, w, 3);
      ctx.fillRect(x, y, 3, h);

      // Árnyék él alul-jobb
      ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.fillRect(x, y + h - 3, w, 3);
      ctx.fillRect(x + w - 3, y, 3, h);
    }
  }

  // Fuga vonalak felülre - vastagabb, mélyített hatás
  for (let i = 0; i <= tileCount; i++) {
    const pos = i * tileSize;
    // Fő fuga
    ctx.fillStyle = '#7a8890';
    ctx.fillRect(pos - groutWidth / 2, 0, groutWidth, size);
    ctx.fillRect(0, pos - groutWidth / 2, size, groutWidth);
    // Fuga árnyék (mélység hatás)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.12)';
    ctx.fillRect(pos - groutWidth / 2, 0, 2, size);
    ctx.fillRect(0, pos - groutWidth / 2, size, 2);
    // Fuga fény (másik oldal)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.fillRect(pos + groutWidth / 2 - 2, 0, 2, size);
    ctx.fillRect(0, pos + groutWidth / 2 - 2, size, 2);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2.5, 2);
  texture.anisotropy = 8;

  return new THREE.MeshStandardMaterial({
    map: texture,
    metalness: 0.25,
    roughness: 0.15,
  });
}

export function createWallMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xd8dfe8,
    metalness: 0.0,
    roughness: 0.85,
  });
}

const SAMPLE_COLORS: Record<string, string> = {
  blood: '#dc2626',
  plasma: '#f59e0b',
  serum: '#10b981',
  dna: '#8b5cf6',
  tissue: '#ec4899',
  urine: '#06b6d4',
};

export function getSampleColor(type: string): string {
  return SAMPLE_COLORS[type] || '#888888';
}

export const SAMPLE_TYPE_LABELS: Record<string, string> = {
  blood: 'Vér',
  plasma: 'Plazma',
  serum: 'Szérum',
  dna: 'DNS',
  tissue: 'Szövet',
  urine: 'Vizelet',
};
